import React, { memo, useMemo, useCallback } from "react";
import { DropdownChip } from "@swiggy-private/dls-web-core/dist/components/Chips/DropwdownChip";
import { InlineChip } from "@swiggy-private/dls-web-core/dist/components/Chips/InlineChip";
import { COMMON_IDS, TEXTS } from "./constants";
import { RefineChip } from "./styles";
import { IFacetList } from "./types";

interface OpenFilterChipsProps {
    facetList: IFacetList[];
    isOpen?: boolean;
    onFacetPillClick: (facet: IFacetList) => void;
    onSelectFacetItem: (id: string, facetId: string, isSelected: boolean | undefined) => void;
    setFilterOpen: (isOpen: boolean) => void;
    setActiveFacet: (facet: IFacetList | null) => void;
}

const OpenFilterChips = ({
    facetList,
    isOpen,
    onFacetPillClick,
    setFilterOpen,
    onSelectFacetItem,
    setActiveFacet,
}: OpenFilterChipsProps) => {
    const renderInlineChip = useCallback(
        (
            id: string,
            label: string,
            facetId: string,
            isSelected: boolean,
            icon?: string,
            selectedIcon?: string,
            key?: string
            // eslint-disable-next-line max-params
        ) => {
            const iconToUse = isSelected ? selectedIcon : icon;
            return (
                <RefineChip id={`filter-chip-${id}`} key={key || id}>
                    <InlineChip
                        label={label}
                        onClick={() => onSelectFacetItem(id, facetId, isSelected)}
                        selectedbgColor="background_Tertiary"
                        isSelected={isSelected}
                        textColor="text_High_Emphasis"
                        logoImageProps={
                            iconToUse ? { cloudinaryId: iconToUse, alt: label } : undefined
                        }
                    />
                </RefineChip>
            );
        },
        [onSelectFacetItem]
    );

    const chips = useMemo(() => {
        return (facetList || []).map((facet, index) => {
            if (!facet) return null;
            if (facet.openFilter) {
                const count = facet.facetInfo?.length || 0;
                if (count > 1) {
                    const isSelected = facet.facetInfo?.some((fi) => fi.selected);
                    return (
                        <RefineChip id={`filter-chip-${facet?.id}`} key={`${facet.id}-${index}`}>
                            <DropdownChip
                                label={facet.label}
                                isSelected={isSelected}
                                isOpen={isOpen}
                                closeOnOverlayClick={true}
                                onClick={() => onFacetPillClick(facet)}
                                ctaText={TEXTS.APPLY}
                                dropdown={{
                                    top: 0,
                                    left: 0,
                                    ctaText: TEXTS.APPLY,
                                    root: `#${COMMON_IDS.SORT_CONTAINER}`,
                                }}
                                onClose={() => {
                                    setActiveFacet(null);
                                    setFilterOpen(false);
                                }}
                                dropdownRoot={`#${COMMON_IDS.SORT_CONTAINER}`}
                            />
                        </RefineChip>
                    );
                } else {
                    const single = facet.facetInfo?.[0];
                    if (!single) return null;
                    const isSelected = !!single.selected;
                    return renderInlineChip(
                        single.id,
                        facet.label,
                        facet.id,
                        isSelected,
                        single.icon || facet.icon,
                        single.icon || facet.selectedIcon,
                        `${facet.id}-${index}`
                    );
                }
            } else {
                // Get all open filter items from facet.facetInfo
                const facetOpenItems =
                    facet.facetInfo?.filter((fi) => fi.openFilter === true) || [];

                if (!facetOpenItems.length) return null;

                return facetOpenItems.map(({ id, label, selected, icon }) => {
                    const isSelected = !!selected;
                    return renderInlineChip(
                        id,
                        label,
                        facet.id,
                        isSelected,
                        icon || facet.icon,
                        icon || facet.selectedIcon
                    );
                });
            }
        });
    }, [facetList, isOpen, onFacetPillClick, setActiveFacet, setFilterOpen, renderInlineChip]);

    return <>{chips}</>;
};

export default memo(OpenFilterChips);
